import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useChat } from '../../hooks/useChat';
import useSubscription from '../../hooks/useSubscription';
import PremiumGate from '../../components/PremiumGate';

const BUSINESS_TYPES = [
  'Plumbing', 'HVAC', 'Electrical', 'Cleaning', 'Landscaping',
  'Pest Control', 'Roofing', 'Painting', 'Pool Service', 'Moving',
  'General Contractor', 'Other'
];

const DESIGN_STYLES = [
  { id: 'modern', label: 'Modern & Clean', desc: 'White background, green accents, minimal', swatch: 'bg-white border-2 border-green-400', textColor: 'text-gray-800' },
  { id: 'bold', label: 'Bold & Professional', desc: 'Dark background, strong typography', swatch: 'bg-gray-900', textColor: 'text-white' },
  { id: 'friendly', label: 'Friendly & Warm', desc: 'Soft colors, rounded elements', swatch: 'bg-orange-50 border-2 border-orange-300', textColor: 'text-orange-800' },
  { id: 'classic', label: 'Classic & Trustworthy', desc: 'Traditional layout, blue/navy tones', swatch: 'bg-navy-800 bg-blue-900', textColor: 'text-white' },
];

const COLOR_SWATCHES = [
  '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#F97316', '#0EA5E9'
];

const DEFAULT_SECTIONS = ['Hero', 'Services', 'About', 'Testimonials', 'Contact Form', 'Phone CTA'];
const OPTIONAL_SECTIONS = ['Gallery', 'Pricing', 'FAQ'];

const ALLOWED_PLANS = ['crm_voice_website', 'crm_voice_website_marketing', 'growth', 'enterprise'];

const WebsiteBuilder = () => {
  const navigate = useNavigate();
  const { organization, profile } = useAuth();
  const { canAccess, isOrgLoading } = useSubscription();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    businessType: '',
    businessName: '',
    city: '',
    description: '',
    phone: '',
    designStyle: 'modern',
    primaryColor: '#22C55E',
    sections: [...DEFAULT_SECTIONS],
  });

  // Preview state
  const [generatedHTML, setGeneratedHTML] = useState('');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editHTML, setEditHTML] = useState('');

  // Publish state
  const [publishStatus, setPublishStatus] = useState('draft'); // draft | deploying | live | error
  const [publishError, setPublishError] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [currentWebsiteId, setCurrentWebsiteId] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Save as draft state
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  // Domain state
  const [customDomain, setCustomDomain] = useState('');
  const [showCNAME, setShowCNAME] = useState(false);
  const [domainVerified, setDomainVerified] = useState(false);
  const [copiedCNAME, setCopiedCNAME] = useState(false);

  // SEO state
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  // Saved sites
  const [savedSites, setSavedSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  // Regenerate confirm
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  // Iterative editing state
  const [editInstruction, setEditInstruction] = useState('');
  const [changeHistory, setChangeHistory] = useState([]);
  const editInputRef = useRef(null);
  const iterativeHTMLRef = useRef('');

  // Claude AI hook for initial generation
  const { response, isLoading: isGenerating, error: aiError, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    true
  );

  // Claude AI hook for iterative edits
  const { response: editResponse, isLoading: isEditing, error: editError, sendMessage: sendEditMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    true
  );

  const generatedHTMLRef = useRef('');

  // Pre-fill from org
  useEffect(() => {
    if (organization) {
      setFormData(prev => ({
        ...prev,
        businessType: organization?.business_type || '',
        businessName: organization?.name || '',
        city: organization?.service_area || '',
      }));
      setMetaTitle(organization?.name || '');
    }
  }, [organization]);

  // Accumulate streaming HTML
  useEffect(() => {
    if (response) {
      generatedHTMLRef.current = response;
    }
  }, [response]);

  // When generation completes, save to Supabase
  useEffect(() => {
    if (!isGenerating && generatedHTMLRef?.current && generatedHTMLRef?.current?.length > 100) {
      const html = generatedHTMLRef?.current;
      setGeneratedHTML(html);
      saveWebsiteToDB(html);
    }
  }, [isGenerating]);

  // Load saved sites
  useEffect(() => {
    if (profile?.org_id) {
      loadSavedSites();
    }
  }, [profile?.org_id]);

  const loadSavedSites = async () => {
    setLoadingSites(true);
    try {
      const { data, error } = await supabase
        ?.from('websites')
        ?.select('id, name, status, created_at, html_content, vercel_deployment_url, form_data')
        ?.eq('org_id', profile?.org_id)
        ?.is('deleted_at', null)
        ?.order('created_at', { ascending: false })
        ?.limit(10);
      if (!error && data) setSavedSites(data);
    } catch (err) {
      console.error('Load sites error:', err);
    } finally {
      setLoadingSites(false);
    }
  };

  const saveWebsiteToDB = async (html, asDraft = false) => {
    if (!profile?.org_id) return null;
    try {
      const siteName = `${formData?.businessName || 'My Site'} - ${new Date()?.toLocaleDateString()}`;
      if (currentWebsiteId && !asDraft) {
        // Update existing
        const { error } = await supabase
          ?.from('websites')
          ?.update({
            html_content: html,
            status: 'draft',
            meta_title: metaTitle || formData?.businessName,
            meta_description: metaDescription || formData?.description?.slice(0, 160),
            form_data: formData,
            updated_at: new Date()?.toISOString(),
          })
          ?.eq('id', currentWebsiteId);
        if (!error) loadSavedSites();
        return currentWebsiteId;
      } else {
        const { data, error } = await supabase
          ?.from('websites')
          ?.insert({
            org_id: profile?.org_id,
            name: siteName,
            business_type: formData?.businessType,
            html_content: html,
            status: 'draft',
            meta_title: metaTitle || formData?.businessName,
            meta_description: metaDescription || formData?.description?.slice(0, 160),
            form_data: formData,
          })
          ?.select('id')
          ?.single();
        if (!error && data) {
          setCurrentWebsiteId(data?.id);
          setPublishStatus('draft');
          loadSavedSites();
          return data?.id;
        }
      }
    } catch (err) {
      console.error('Save website error:', err);
    }
    return null;
  };

  const handleSaveAsDraft = async () => {
    if (!generatedHTML) return;
    setSavingDraft(true);
    setDraftSaved(false);
    try {
      await saveWebsiteToDB(generatedHTML, true);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (err) {
      console.error('Save draft error:', err);
    } finally {
      setSavingDraft(false);
    }
  };

  const buildPrompt = () => {
    const sectionsStr = formData?.sections?.join(', ');
    return `Generate a complete, professional, single-page HTML website for a ${formData?.businessType} business called "${formData?.businessName}" located in ${formData?.city}.

Business description: ${formData?.description}
Phone: ${formData?.phone}
Design style: ${formData?.designStyle}
Primary color: ${formData?.primaryColor}
Sections to include: ${sectionsStr}

Requirements:
- Return ONLY pure HTML with all CSS embedded in <style> tags and all JS embedded in <script> tags
- No external dependencies except Google Fonts (allowed via CDN link)
- Mobile responsive with proper meta viewport tag
- Hero section with headline, subheadline, and CTA button calling ${formData?.phone}
- Services section with 4-6 service cards relevant to ${formData?.businessType}
- About section with trust signals and years of experience
- Contact form with name, phone, email, message fields
- Sticky header with business name and phone number
- Footer with copyright, phone, and service area
- Professional, conversion-optimized layout
- Smooth scroll navigation
- Use ${formData?.primaryColor} as the accent/CTA color
- Do NOT include any placeholder lorem ipsum text — use realistic content for a ${formData?.businessType} in ${formData?.city}`;
  };

  const handleGenerate = () => {
    generatedHTMLRef.current = '';
    setGeneratedHTML('');
    setPublishStatus('draft');
    setPublishError('');
    setLiveUrl('');
    setCurrentWebsiteId(null);
    setShowRegenConfirm(false);
    const prompt = buildPrompt();
    sendMessage(
      [{ role: 'user', content: prompt }],
      { max_tokens: 8000, temperature: 0.7 }
    );
  };

  const handleRegenerateClick = () => {
    if (generatedHTML) {
      setShowRegenConfirm(true);
    } else {
      handleGenerate();
    }
  };

  const handleSectionToggle = (section) => {
    setFormData(prev => ({
      ...prev,
      sections: prev?.sections?.includes(section)
        ? prev?.sections?.filter(s => s !== section)
        : [...prev?.sections, section],
    }));
  };

  const handleLoadSite = (site) => {
    setGeneratedHTML(site?.html_content || '');
    setCurrentWebsiteId(site?.id);
    setPublishStatus(site?.status === 'live' ? 'live' : 'draft');
    setLiveUrl(site?.vercel_deployment_url || '');
    setPublishError('');
    if (site?.form_data) {
      setFormData(prev => ({ ...prev, ...site?.form_data }));
    }
  };

  const injectSEO = (html) => {
    if (!metaTitle && !metaDescription) return html;
    const seoTags = `<title>${metaTitle}</title>\n<meta name="description" content="${metaDescription}">`;
    if (html?.includes('<head>')) {
      return html?.replace('<head>', `<head>\n${seoTags}`);
    }
    return html;
  };

  const handlePublish = async () => {
    if (!generatedHTML) return;

    setPublishStatus('deploying');
    setPublishError('');
    const orgIdShort = profile?.org_id?.slice(0, 8) || 'wilcart';
    const projectName = `wilcart-${orgIdShort}`;
    const finalHTML = injectSEO(generatedHTML);

    try {
      // Server-side proxy keeps the Vercel API token out of the client bundle.
      const { data: deployData, error: deployErr } = await supabase?.functions?.invoke('vercel-deploy', {
        body: { action: 'publish', html: finalHTML, project_name: projectName },
      });

      if (deployErr || deployData?.error) {
        throw new Error(deployData?.error || deployErr?.message || 'Deployment failed');
      }

      const deploymentId = deployData?.deployment_id;
      const deploymentUrl = deployData?.url;

      if (!deploymentId) throw new Error('No deployment_id returned from vercel-deploy');

      // Update DB with deploying status
      if (currentWebsiteId) {
        await supabase?.from('websites')?.update({
          status: 'deploying',
          vercel_deployment_id: deploymentId,
        })?.eq('id', currentWebsiteId);
      }

      // Poll deployment status via edge function
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const { data: statusData, error: statusErr } = await supabase?.functions?.invoke('vercel-deploy', {
            body: { action: 'status', deployment_id: deploymentId },
          });
          if (statusErr) {
            console.error('Status poll error:', statusErr);
            return;
          }

          if (statusData?.readyState === 'READY') {
            clearInterval(pollInterval);
            const rawUrl = statusData?.url || deploymentUrl;
            const finalUrl = rawUrl?.startsWith('http') ? rawUrl : `https://${rawUrl}`;
            setLiveUrl(finalUrl);
            setPublishStatus('live');
            setPublishError('');

            if (currentWebsiteId) {
              await supabase?.from('websites')?.update({
                status: 'live',
                vercel_deployment_url: finalUrl,
                updated_at: new Date()?.toISOString(),
              })?.eq('id', currentWebsiteId);
            }
            loadSavedSites();
          } else if (statusData?.readyState === 'ERROR' || statusData?.readyState === 'CANCELED') {
            clearInterval(pollInterval);
            setPublishStatus('error');
            setPublishError('Deployment failed on Vercel. Please check your Vercel dashboard for details.');
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setPublishStatus('error');
            setPublishError('Deployment timed out after 90 seconds. Check your Vercel dashboard.');
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
        }
      }, 3000);
    } catch (err) {
      console.error('Publish error:', err);
      setPublishStatus('error');
      setPublishError(err?.message || 'An unexpected error occurred during deployment.');
    }
  };

  const handleConnectDomain = () => {
    if (!customDomain?.trim()) return;
    if (publishStatus !== 'live') {
      setPublishError('Please publish your website first before connecting a custom domain.');
      return;
    }
    setShowCNAME(true);
  };

  const handleVerifyDomain = async () => {
    if (!currentWebsiteId) return;
    try {
      const orgIdShort = profile?.org_id?.slice(0, 8) || 'wilcart';
      const projectName = `wilcart-${orgIdShort}`;
      const { data, error } = await supabase?.functions?.invoke('vercel-deploy', {
        body: { action: 'verify_domain', project_name: projectName, domain: customDomain },
      });
      if (error || data?.error) {
        setPublishError(data?.error || error?.message || 'Failed to verify domain. Please try again.');
        return;
      }
      if (data?.verified) {
        setDomainVerified(true);
        await supabase?.from('websites')?.update({
          custom_domain: customDomain,
          domain_verified: true,
        })?.eq('id', currentWebsiteId);
      } else {
        setPublishError('Domain not verified yet. Please check your DNS settings and try again in a few minutes.');
      }
    } catch (err) {
      console.error('Domain verify error:', err);
      setPublishError('Failed to verify domain. Please try again.');
    }
  };

  const copyToClipboard = (text, setCopied) => {
    navigator?.clipboard?.writeText(text)?.then(() => {
      if (setCopied) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  };

  const getPreviewWidth = () => {
    if (previewDevice === 'mobile') return '375px';
    if (previewDevice === 'tablet') return '768px';
    return '100%';
  };

  const statusBadge = (status) => {
    if (status === 'live') return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Live</span>;
    if (status === 'deploying') return <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Deploying</span>;
    return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Draft</span>;
  };

  // Line numbers for edit modal
  const getLineNumbers = (text) => {
    const lines = (text || '')?.split('\n');
    return lines?.map((_, i) => i + 1)?.join('\n');
  };

  const handleEditSubmit = (e) => {
    e?.preventDefault();
    if (!editInstruction?.trim() || isEditing || !generatedHTML) return;

    const instruction = editInstruction?.trim();
    setChangeHistory(prev => [{ instruction, timestamp: new Date() }, ...prev]);
    setEditInstruction('');
    iterativeHTMLRef.current = '';

    const prompt = `Current website HTML:\n${generatedHTML}\n\nClient wants to change: ${instruction}\n\nReturn only complete updated HTML, nothing else.`;
    sendEditMessage(
      [{ role: 'user', content: prompt }],
      { max_tokens: 8000, temperature: 0.3 }
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Website Builder - Wilcart CRM</title>
        <meta name="description" content="AI-powered website builder for your business" />
      </Helmet>
      <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <main className="lg:ml-64 pt-24 lg:pt-28 h-[calc(100vh-112px)] overflow-hidden">
        {isOrgLoading ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !canAccess('websiteBuilder') ? (
          <PremiumGate
            featureName="Website Builder"
            featureIcon="Globe"
            description="Build and launch AI-powered websites with a drag-and-drop editor, SEO tools, custom domain, and managed hosting — all in minutes."
            requiredPlan="CRM + Voice + Website"
            planId="crm_voice_website"
          />
        ) : (
          <div className="flex h-full">
            {/* LEFT PANEL */}
            <div className="w-80 flex-shrink-0 border-r border-border bg-background overflow-y-auto">
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between mb-1">
                  <h1 className="text-lg font-bold text-foreground">Website Builder</h1>
                  <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-700 rounded-full">Powered by Claude AI</span>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Business Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Business Type</label>
                  <select
                    value={formData?.businessType}
                    onChange={e => setFormData(prev => ({ ...prev, businessType: e?.target?.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Select type...</option>
                    {BUSINESS_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Business Name</label>
                  <input
                    type="text"
                    value={formData?.businessName}
                    onChange={e => setFormData(prev => ({ ...prev, businessName: e?.target?.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="Your business name"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">City / Service Area</label>
                  <input
                    type="text"
                    value={formData?.city}
                    onChange={e => setFormData(prev => ({ ...prev, city: e?.target?.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="e.g. Austin, TX"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Business Description</label>
                  <textarea
                    value={formData?.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                    placeholder="Describe your services, experience, what makes you unique..."
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={formData?.phone}
                    onChange={e => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="(555) 000-0000"
                  />
                </div>

                {/* Design Style */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Design Style</label>
                  <div className="grid grid-cols-2 gap-2">
                    {DESIGN_STYLES?.map(style => (
                      <button
                        key={style?.id}
                        onClick={() => setFormData(prev => ({ ...prev, designStyle: style?.id }))}
                        className={`p-2 rounded-lg border-2 text-left transition-all ${
                          formData?.designStyle === style?.id
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-border hover:border-violet-300'
                        }`}
                      >
                        <div className={`w-full h-6 rounded mb-1 ${style?.swatch}`} />
                        <p className="text-xs font-medium text-foreground leading-tight">{style?.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Scheme */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Primary Color</label>
                  <div className="flex items-center space-x-2">
                    {COLOR_SWATCHES?.map(color => (
                      <button
                        key={color}
                        onClick={() => setFormData(prev => ({ ...prev, primaryColor: color }))}
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          formData?.primaryColor === color ? 'border-foreground scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Sections */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Sections to Include</label>
                  <div className="space-y-1.5">
                    {[...DEFAULT_SECTIONS, ...OPTIONAL_SECTIONS]?.map(section => (
                      <label key={section} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData?.sections?.includes(section)}
                          onChange={() => handleSectionToggle(section)}
                          className="w-4 h-4 rounded border-border text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm text-foreground">{section}</span>
                        {DEFAULT_SECTIONS?.includes(section) && (
                          <span className="text-xs text-muted-foreground">(recommended)</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !formData?.businessType || !formData?.businessName}
                  className="w-full py-3 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Claude is generating your website...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Sparkles" size={16} />
                      <span>Generate Website</span>
                    </>
                  )}
                </button>
              </div>

              {/* Saved Sites */}
              {savedSites?.length > 0 && (
                <div className="p-4 border-t border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Generated Sites</h3>
                  <div className="space-y-2">
                    {savedSites?.map(site => (
                      <div key={site?.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="text-xs font-medium text-foreground truncate">{site?.name}</p>
                          <div className="flex items-center space-x-1 mt-0.5">
                            {statusBadge(site?.status)}
                            <span className="text-xs text-muted-foreground">
                              {new Date(site?.created_at)?.toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleLoadSite(site)}
                          className="text-xs px-2 py-1 bg-violet-100 text-violet-700 hover:bg-violet-200 rounded transition-colors flex-shrink-0"
                        >
                          Load
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CENTER PANEL */}
            <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-4 py-2 bg-background border-b border-border">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-foreground">Preview</span>
                  <div className="flex items-center bg-muted rounded-lg p-0.5">
                    {[
                      { id: 'desktop', icon: 'Monitor', label: 'Desktop' },
                      { id: 'tablet', icon: 'Tablet', label: 'Tablet' },
                      { id: 'mobile', icon: 'Smartphone', label: 'Mobile' }
                    ]?.map(d => (
                      <button
                        key={d?.id}
                        onClick={() => setPreviewDevice(d?.id)}
                        title={d?.label}
                        className={`p-1.5 rounded-md transition-colors ${
                          previewDevice === d?.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon name={d?.icon} size={14} />
                      </button>
                    ))}
                  </div>
                  {generatedHTML && (
                    <span className="text-xs text-muted-foreground">
                      {previewDevice === 'desktop' ? 'Full width' : previewDevice === 'tablet' ? '768px' : '375px'}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {generatedHTML && (
                    <>
                      <button
                        onClick={handleRegenerateClick}
                        disabled={isGenerating}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        <Icon name="RefreshCw" size={12} />
                        <span>Regenerate</span>
                      </button>
                      <button
                        onClick={() => { setEditHTML(generatedHTML); setShowEditModal(true); }}
                        className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                      >
                        <Icon name="Code" size={12} />
                        <span>Edit HTML</span>
                      </button>
                      <button
                        onClick={handleSaveAsDraft}
                        disabled={savingDraft}
                        className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors ${
                          draftSaved
                            ? 'border-green-300 text-green-700 bg-green-50' :'text-muted-foreground hover:text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {savingDraft ? (
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <Icon name={draftSaved ? 'CheckCircle' : 'Save'} size={12} />
                        )}
                        <span>{draftSaved ? 'Saved!' : 'Save Draft'}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Preview Area */}
              <div className="flex-1 overflow-auto flex items-start justify-center p-4">
                {isGenerating && !generatedHTML ? (
                  <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                      <svg className="animate-spin w-6 h-6 text-violet-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-foreground">Claude is crafting your website...</p>
                      <p className="text-sm text-muted-foreground mt-1">This usually takes 15-30 seconds</p>
                    </div>
                    <div className="w-full max-w-2xl space-y-3 mt-4">
                      <div className="h-48 bg-gray-200 rounded-lg animate-pulse" />
                      <div className="grid grid-cols-3 gap-3">
                        {[1,2,3]?.map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />)}
                      </div>
                      <div className="h-32 bg-gray-200 rounded-lg animate-pulse" />
                    </div>
                  </div>
                ) : generatedHTML ? (
                  <div
                    className="transition-all duration-300"
                    style={{
                      width: getPreviewWidth(),
                      maxWidth: '100%',
                      border: previewDevice !== 'desktop' ? '8px solid #1f2937' : 'none',
                      borderRadius: previewDevice !== 'desktop' ? '16px' : '0',
                      overflow: 'hidden',
                      boxShadow: previewDevice !== 'desktop' ? '0 20px 60px rgba(0,0,0,0.3)' : 'none',
                    }}
                  >
                    <iframe
                      srcDoc={generatedHTML}
                      title="Website Preview"
                      className="w-full"
                      style={{ height: previewDevice === 'mobile' ? '667px' : '800px', border: 'none' }}
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                    <div className="w-20 h-20 bg-violet-50 rounded-2xl flex items-center justify-center">
                      <Icon name="Globe" size={40} className="text-violet-300" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-foreground">Your website preview will appear here</p>
                      <p className="text-sm text-muted-foreground mt-1">Fill in the form and click "Generate Website" to get started</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Iterative Edit Chat Input - shown only after website is generated */}
              {generatedHTML && (
                <div className="border-t border-border bg-background">
                  {/* Change History */}
                  {changeHistory?.length > 0 && (
                    <div className="px-4 pt-3 pb-1">
                      <div className="flex items-center space-x-1.5 mb-2">
                        <Icon name="History" size={12} className="text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">Recent changes</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-16 overflow-y-auto">
                        {changeHistory?.slice(0, 8)?.map((item, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded-full truncate max-w-[200px]"
                            title={item?.instruction}
                          >
                            {item?.instruction?.length > 30 ? item?.instruction?.slice(0, 30) + '…' : item?.instruction}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input Row */}
                  <form onSubmit={handleEditSubmit} className="flex items-center space-x-2 px-4 py-3">
                    <div className="flex-1 relative">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editInstruction}
                        onChange={e => setEditInstruction(e?.target?.value)}
                        placeholder={isEditing ? 'Applying changes...' : 'Change headline · Add pricing · Change colors · Update phone number'}
                        disabled={isEditing}
                        className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60 disabled:cursor-not-allowed pr-10"
                      />
                      {isEditing && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin w-4 h-4 text-violet-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={!editInstruction?.trim() || isEditing}
                      className="flex items-center space-x-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                    >
                      {isEditing ? (
                        <span>Updating...</span>
                      ) : (
                        <>
                          <Icon name="Wand2" size={14} />
                          <span>Apply</span>
                        </>
                      )}
                    </button>
                  </form>

                  {isEditing && (
                    <div className="px-4 pb-2">
                      <p className="text-xs text-violet-600 flex items-center space-x-1">
                        <Icon name="Sparkles" size={11} />
                        <span>Claude is updating your website...</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT PANEL */}
            <div className="w-72 flex-shrink-0 border-l border-border bg-background overflow-y-auto">
              {/* Publish Section */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Rocket" size={16} className="text-violet-600" />
                  <h3 className="text-sm font-semibold text-foreground">Publish Website</h3>
                </div>

                {/* Status Card */}
                <div className={`p-3 rounded-lg mb-3 ${
                  publishStatus === 'live' ? 'bg-green-50 border border-green-200' :
                  publishStatus === 'deploying' ? 'bg-yellow-50 border border-yellow-200' :
                  publishStatus === 'error'? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-start space-x-2">
                    {publishStatus === 'live' && <Icon name="CheckCircle" size={14} className="text-green-600 mt-0.5 flex-shrink-0" />}
                    {publishStatus === 'deploying' && (
                      <svg className="animate-spin w-3.5 h-3.5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {publishStatus === 'error' && <Icon name="AlertCircle" size={14} className="text-red-600 mt-0.5 flex-shrink-0" />}
                    {publishStatus === 'draft' && <Icon name="FileText" size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-medium ${
                        publishStatus === 'live' ? 'text-green-700' :
                        publishStatus === 'deploying' ? 'text-yellow-700' :
                        publishStatus === 'error'? 'text-red-700' : 'text-gray-600'
                      }`}>
                        {publishStatus === 'live' ? 'Site is live!' :
                         publishStatus === 'deploying' ? 'Deploying to Vercel...' :
                         publishStatus === 'error'? 'Deployment failed' : 'Not published yet'}
                      </span>
                      {publishStatus === 'error' && publishError && (
                        <p className="text-xs text-red-600 mt-1 leading-relaxed">{publishError}</p>
                      )}
                    </div>
                  </div>
                </div>

                {publishStatus === 'error' && (
                  <button
                    onClick={() => { setPublishStatus('draft'); setPublishError(''); }}
                    className="w-full mt-2 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                  >
                    Dismiss error
                  </button>
                )}
              </div>

              {/* Custom Domain - only show after live */}
              <div className="p-4 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground mb-1">Custom Domain</h3>
                {publishStatus !== 'live' && (
                  <p className="text-xs text-muted-foreground mb-3">Publish your site first to connect a custom domain.</p>
                )}
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    value={customDomain}
                    onChange={e => setCustomDomain(e?.target?.value)}
                    placeholder="yourdomain.com"
                    disabled={publishStatus !== 'live'}
                    className="flex-1 px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={handleConnectDomain}
                    disabled={publishStatus !== 'live' || !customDomain?.trim()}
                    className="px-3 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors whitespace-nowrap"
                  >
                    Connect
                  </button>
                </div>

                {showCNAME && publishStatus === 'live' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs space-y-2">
                    <div className="flex items-center space-x-1 mb-1">
                      <Icon name="Info" size={12} className="text-blue-600" />
                      <p className="font-semibold text-blue-800">Add DNS record to your domain registrar:</p>
                    </div>
                    <div className="bg-white rounded border border-blue-200 overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-blue-100">
                            <th className="text-left px-2 py-1 text-blue-700 font-medium">Type</th>
                            <th className="text-left px-2 py-1 text-blue-700 font-medium">Name</th>
                            <th className="text-left px-2 py-1 text-blue-700 font-medium">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="px-2 py-1.5 font-mono text-blue-800">CNAME</td>
                            <td className="px-2 py-1.5 font-mono text-blue-800">www</td>
                            <td className="px-2 py-1.5 font-mono text-blue-800">cname.vercel-dns.com</td>
                          </tr>
                          <tr className="bg-blue-50">
                            <td className="px-2 py-1.5 font-mono text-blue-800">A</td>
                            <td className="px-2 py-1.5 font-mono text-blue-800">@</td>
                            <td className="px-2 py-1.5 font-mono text-blue-800">76.76.21.21</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-blue-600">DNS changes can take up to 48 hours to propagate.</p>
                    <button
                      onClick={handleVerifyDomain}
                      className={`w-full py-1.5 text-xs rounded-lg transition-colors font-medium ${
                        domainVerified
                          ? 'bg-green-100 text-green-700 border border-green-300' :'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {domainVerified ? '✓ Domain Verified' : 'Verify Domain'}
                    </button>
                  </div>
                )}
              </div>

              {/* SEO Settings */}
              <div className="p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">SEO Settings</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Meta Title</label>
                    <input
                      type="text"
                      value={metaTitle}
                      onChange={e => setMetaTitle(e?.target?.value)}
                      className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Page title for search engines"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Meta Description</label>
                    <textarea
                      value={metaDescription}
                      onChange={e => setMetaDescription(e?.target?.value)}
                      rows={3}
                      className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                      placeholder="Brief description for search results..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">{metaDescription?.length}/160 chars</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      {/* Edit HTML Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-5xl mx-4 flex flex-col" style={{ height: '85vh' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-gray-950 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="flex space-x-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <span className="text-sm font-medium text-gray-300">index.html</span>
                <span className="text-xs text-gray-500">{editHTML?.split('\n')?.length} lines</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyToClipboard(editHTML, null)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <Icon name="Copy" size={12} />
                  <span>Copy All</span>
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-200 p-1 rounded transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {/* Code Editor Area */}
            <div className="flex-1 flex overflow-hidden bg-gray-950">
              {/* Line Numbers */}
              <div
                className="select-none text-right pr-4 pl-4 py-4 text-gray-600 font-mono text-xs leading-5 bg-gray-950 border-r border-gray-800 overflow-hidden"
                style={{ minWidth: '52px', userSelect: 'none' }}
                aria-hidden="true"
              >
                {(editHTML || '')?.split('\n')?.map((_, i) => (
                  <div key={i} className="leading-5">{i + 1}</div>
                ))}
              </div>
              {/* Textarea */}
              <textarea
                value={editHTML}
                onChange={e => setEditHTML(e?.target?.value)}
                className="flex-1 p-4 font-mono text-xs text-green-300 bg-gray-950 resize-none focus:outline-none leading-5"
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                style={{ tabSize: 2 }}
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-background rounded-b-xl">
              <p className="text-xs text-muted-foreground">
                Edit the HTML directly. Changes will update the preview.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setGeneratedHTML(editHTML); setShowEditModal(false); }}
                  className="px-4 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Icon name="Check" size={14} />
                  <span>Apply Changes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Regenerate Confirmation Modal */}
      {showRegenConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="RefreshCw" size={18} className="text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Regenerate Website?</h3>
                <p className="text-sm text-muted-foreground">This will replace your current website with a new AI-generated version.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Any unsaved edits will be lost. Consider saving as a draft first.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowRegenConfirm(false)}
                className="flex-1 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="flex-1 py-2 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteBuilder;
